import React, { useEffect, useRef, useState } from "react";
import { Loader2, Send, Award, RotateCcw, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

function ScoreRow({ label, value }) {
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1"><span className="text-muted-foreground">{label}</span><span className="font-semibold text-foreground">{value}</span></div>
      <Progress value={value} className="h-2" />
    </div>
  );
}

export function RolePlay() {
  const { t, lang } = useI18n();
  const [scenarios, setScenarios] = useState([]);
  const [session, setSession] = useState(null);
  const [turns, setTurns] = useState([]);
  const [input, setInput] = useState("");
  const [starting, setStarting] = useState(null);
  const [sending, setSending] = useState(false);
  const [canFinish, setCanFinish] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [scores, setScores] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { api.get("/learning/roleplay/scenarios").then(({ data }) => setScenarios(data.scenarios || [])).catch((e) => console.error("Failed to load scenarios:", e)); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [turns]);

  const start = async (sc) => {
    setStarting(sc.id);
    try {
      const { data } = await api.post("/learning/roleplay/start", { scenario_id: sc.id });
      setSession(data); setTurns(data.turns); setScores(null); setCanFinish(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setStarting(null); }
  };

  const send = async () => {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput(""); setSending(true);
    try {
      const { data } = await api.post("/learning/roleplay/respond", { session_id: session.session_id, text });
      setTurns((ts) => [...ts, data.agent_turn, data.customer_turn]);
      setCanFinish(data.can_finish);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); setInput(text); }
    finally { setSending(false); }
  };

  const finish = async () => {
    setScoring(true);
    try {
      const { data } = await api.post(`/learning/roleplay/${session.session_id}/score`);
      setScores(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setScoring(false); }
  };

  const reset = () => { setSession(null); setTurns([]); setScores(null); setCanFinish(false); };

  if (!session) {
    return (
      <div>
        <p className="text-sm text-muted-foreground mb-4">{t("rolePlayIntro")}</p>
        <div className="grid sm:grid-cols-2 gap-4" data-testid="scenario-grid">
          {scenarios.map((sc) => (
            <button key={sc.id} onClick={() => start(sc)} disabled={starting} data-testid={`scenario-${sc.id}`}
              className="text-start bg-white border border-border rounded-2xl p-5 hover:border-accent/50 transition-colors disabled:opacity-60">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{sc.icon}</span>
                <div>
                  <div className="font-head font-semibold text-foreground">{lang === "ar" ? sc.title_ar : sc.title_en}</div>
                  <div className="text-xs text-muted-foreground">{lang === "ar" ? sc.desc_ar : sc.desc_en}</div>
                </div>
                {starting === sc.id && <Loader2 className="animate-spin ms-auto text-accent" size={18} />}
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-border rounded-2xl overflow-hidden" data-testid="roleplay-chat">
      <div className="flex items-center justify-between gap-2 p-4 border-b border-border bg-secondary/40">
        <div className="min-w-0">
          <div className="font-head font-semibold text-foreground truncate">{session.scenario_title}</div>
          <div className="text-xs text-muted-foreground truncate">{session.persona}</div>
        </div>
        <button onClick={reset} data-testid="roleplay-new-btn" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary shrink-0"><RotateCcw size={15} /> {t("newScenario")}</button>
      </div>

      <div className="p-4 space-y-4 max-h-[46vh] overflow-y-auto scroll-thin chat-pattern">
        {turns.map((turn, i) => {
          const isAgent = turn.role === "agent";
          return (
            <div key={i} className={`flex ${isAgent ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${isAgent ? "bg-accent text-white" : "bg-white border border-border"}`}>
                <div className="text-[10px] uppercase tracking-wide opacity-70 mb-0.5">{isAgent ? t("youLabel") : t("customerLabel")}</div>
                <div className="text-[15px]" dir={turn.language === "ar" ? "rtl" : "ltr"}>{turn.text}</div>
                {turn.translation && <div className={`text-xs mt-1 ${isAgent ? "text-white/70" : "text-muted-foreground"}`} dir={turn.language === "ar" ? "ltr" : "rtl"}>{turn.translation}</div>}
              </div>
            </div>
          );
        })}
        {sending && <div className="flex justify-start"><div className="rounded-2xl bg-white border border-border px-4 py-2.5"><Loader2 className="animate-spin text-muted-foreground" size={16} /></div></div>}
        <div ref={endRef} />
      </div>

      {scores ? (
        <div className="p-5 border-t border-border" data-testid="roleplay-scorecard">
          <h4 className="font-head font-semibold text-foreground flex items-center gap-2 mb-4"><Award size={18} className="text-accent" /> {t("scorecard")}</h4>
          <div className="space-y-3">
            <ScoreRow label={t("clarity")} value={scores.clarity} />
            <ScoreRow label={t("politeness")} value={scores.politeness} />
            <ScoreRow label={t("completeness")} value={scores.completeness} />
          </div>
          {scores.feedback && <p className="text-sm text-muted-foreground mt-4 italic">“{scores.feedback}”</p>}
          <button onClick={reset} data-testid="roleplay-restart-btn" className="mt-4 h-10 px-5 rounded-xl bg-primary text-white font-medium hover:bg-primary/90">{t("newScenario")}</button>
        </div>
      ) : (
        <div className="p-3 border-t border-border">
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={t("typeReply")}
              rows={1}
              data-testid="roleplay-input"
              className="flex-1 resize-none rounded-2xl border border-border px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-accent/40 max-h-28"
            />
            <button onClick={send} disabled={sending || !input.trim()} data-testid="roleplay-send-btn" className="h-12 w-12 shrink-0 grid place-items-center rounded-full bg-accent text-white disabled:opacity-50 hover:bg-accent/90">
              {sending ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} className="rtl:rotate-180" />}
            </button>
          </div>
          {canFinish && (
            <button onClick={finish} disabled={scoring} data-testid="roleplay-finish-btn" className="mt-2 w-full h-11 rounded-xl bg-primary text-white font-medium hover:bg-primary/90 flex items-center justify-center gap-2">
              {scoring ? <Loader2 className="animate-spin" size={17} /> : <><Award size={17} /> {t("finishScore")}</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
